import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { PRODUCT_CATEGORIES, STORE_TYPES, lookup } from "@/lib/grocery";
import { cart } from "@/lib/cart";
import { toast } from "sonner";
import {
  ArrowRight, MapPin, Phone, Plus, Minus, ShoppingCart, Trash2, Loader2,
  Send, Tag
} from "lucide-react";

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkout, setCheckout] = useState({
    delivery_address: "", delivery_city: "", delivery_state: "", delivery_zip: "",
    delivery_phone: user?.phone || "", delivery_notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stores/${id}`),
      axios.get(`${API}/stores/${id}/products`)
    ]).then(([s, p]) => {
      setStore(s.data); setProducts(p.data);
    }).catch(() => {}).finally(() => setLoading(false));
    setCartItems(cart.get(id));
  }, [id]);

  const refreshCart = () => setCartItems(cart.get(id));
  const add = (p) => { cart.add(id, p, 1); refreshCart(); toast.success(t("أُضيف للسلة", "Added to cart")); };
  const dec = (pid, q) => { cart.updateQty(id, pid, q - 1); refreshCart(); };
  const inc = (pid, q) => { cart.updateQty(id, pid, q + 1); refreshCart(); };
  const remove = (pid) => { cart.remove(id, pid); refreshCart(); };

  const subtotal = cartItems.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);
  const fee = store?.delivery_fee || 5.99;
  const total = subtotal + fee;

  const filtered = category ? products.filter((p) => p.category === category) : products;

  const submit = async () => {
    if (!checkout.delivery_address || !checkout.delivery_city || !checkout.delivery_state || !checkout.delivery_zip || !checkout.delivery_phone) {
      toast.error(t("يرجى تعبئة عنوان التوصيل", "Please complete delivery info")); return;
    }
    if (cartItems.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        store_id: id, order_type: "products",
        items: cartItems.map((i) => ({
          product_id: i.product_id, name: i.name, quantity: i.quantity, unit: i.unit, price: i.price
        })),
        ...checkout
      };
      const res = await axios.post(`${API}/orders`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم استلام طلبك!", "Order placed!"));
      cart.clear(id);
      navigate(`/grocery/orders`);
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل الطلب", "Order failed")); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-yellow-500 outline-none";

  if (loading) return <DashboardLayout title=""><div className="glass-card rounded-2xl h-96 skeleton" /></DashboardLayout>;
  if (!store) return <DashboardLayout title={t("غير موجود", "Not found")}><div className="glass-card rounded-2xl p-12 text-center text-slate-400">{t("المتجر غير موجود", "Store not found")}</div></DashboardLayout>;

  return (
    <DashboardLayout title={language === "ar" ? (store.name_ar || store.name) : store.name}>
      <div className="space-y-6" data-testid="store-detail">
        <button onClick={() => navigate("/grocery")} className="inline-flex items-center gap-2 text-yellow-500 hover:underline">
          <ArrowRight size={16} /> {t("رجوع للمتاجر", "Back to stores")}
        </button>

        {/* Store header */}
        <div className="glass-card rounded-2xl p-6 flex flex-col md:flex-row gap-5 purple-gold-glow">
          <div className="w-32 h-32 rounded-2xl overflow-hidden bg-slate-900 flex-shrink-0">
            <ListingImage path={store.logo || store.photos?.[0]} alt={store.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <Badge className="bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0 mb-2">
              {lookup(STORE_TYPES, store.store_type, language)}
            </Badge>
            <h1 className="text-2xl font-bold text-white mb-1">{language === "ar" ? (store.name_ar || store.name) : store.name}</h1>
            <p className="text-slate-400 mb-2">{store.description}</p>
            <div className="flex gap-4 text-sm text-slate-400 flex-wrap">
              <span className="flex items-center gap-1"><MapPin size={14} className="text-yellow-500" />{store.address}, {store.city}, {store.state} {store.zip_code}</span>
              <span className="flex items-center gap-1"><Phone size={14} className="text-yellow-500" />{store.phone}</span>
              <span className="text-yellow-400">{t("التوصيل:", "Delivery:")} ${store.delivery_fee || 5.99}</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Products */}
          <div className="lg:col-span-2 space-y-4">
            {/* Category chips */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button onClick={() => setCategory("")} className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap ${category === "" ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`}>
                {t("الكل", "All")}
              </button>
              {PRODUCT_CATEGORIES.map((c) => (
                <button key={c.v} onClick={() => setCategory(c.v)} className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap ${category === c.v ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
                  {language === "ar" ? c.ar : c.en}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
                {t("لا توجد منتجات بعد. جرّب إنشاء قائمة تسوق.", "No products yet. Try a shopping list.")}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map((p) => (
                  <div key={p.product_id} className="glass-card rounded-2xl p-3 flex gap-3" data-testid={`product-${p.product_id}`}>
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-900 flex-shrink-0">
                      <ListingImage path={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate text-sm">{language === "ar" ? (p.name_ar || p.name) : p.name}</h3>
                      <p className="text-xs text-slate-500 truncate">{lookup(PRODUCT_CATEGORIES, p.category, language)}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-yellow-400 font-bold">${p.price}/{p.unit}</span>
                        <Button size="sm" onClick={() => add(p)} disabled={!p.in_stock} className="btn-primary h-8 px-3" data-testid={`add-${p.product_id}`}>
                          <Plus size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <div>
            <div className="glass-card rounded-2xl p-5 sticky top-4 purple-gold-glow" data-testid="cart-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <ShoppingCart size={18} className="text-yellow-500" /> {t("سلة الشراء", "Cart")}
                </h3>
                {cartItems.length > 0 && (
                  <button onClick={() => { cart.clear(id); refreshCart(); }} className="text-red-400 text-xs">{t("إفراغ", "Clear")}</button>
                )}
              </div>
              {cartItems.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">{t("السلة فارغة", "Empty cart")}</p>
              ) : (
                <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
                  {cartItems.map((i) => (
                    <div key={i.product_id} className="bg-slate-900 rounded-xl p-2 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{i.name}</p>
                        <p className="text-yellow-400 text-xs">${(i.price * i.quantity).toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => dec(i.product_id, i.quantity)} className="w-6 h-6 rounded bg-slate-800 text-white"><Minus size={12} className="mx-auto" /></button>
                        <span className="w-6 text-center text-white text-sm">{i.quantity}</span>
                        <button onClick={() => inc(i.product_id, i.quantity)} className="w-6 h-6 rounded bg-slate-800 text-white"><Plus size={12} className="mx-auto" /></button>
                      </div>
                      <button onClick={() => remove(i.product_id)} className="text-red-400"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              {cartItems.length > 0 && (
                <>
                  <div className="border-t border-slate-800 pt-3 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-400"><span>{t("المجموع", "Subtotal")}</span><span>${subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between text-slate-400"><span>{t("التوصيل", "Delivery")}</span><span>${fee}</span></div>
                    <div className="flex justify-between text-white font-bold pt-1"><span>{t("الإجمالي", "Total")}</span><span className="text-yellow-400">${total.toFixed(2)}</span></div>
                  </div>
                  <Button onClick={() => setShowCheckout(true)} className="w-full btn-primary mt-3" data-testid="checkout-btn">
                    {t("إتمام الشراء", "Checkout")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Checkout modal-like panel */}
        {showCheckout && (
          <div className="glass-card rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-white">{t("معلومات التوصيل", "Delivery info")}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <input className={inputCls} placeholder={t("العنوان", "Address")} value={checkout.delivery_address} onChange={(e) => setCheckout({...checkout, delivery_address: e.target.value})} data-testid="ck-address" />
              <input className={inputCls} placeholder={t("المدينة", "City")} value={checkout.delivery_city} onChange={(e) => setCheckout({...checkout, delivery_city: e.target.value})} data-testid="ck-city" />
              <input className={inputCls} placeholder={t("الولاية (مثلاً NY)", "State (e.g. NY)")} value={checkout.delivery_state} onChange={(e) => setCheckout({...checkout, delivery_state: e.target.value})} data-testid="ck-state" />
              <input className={inputCls} placeholder={t("ZIP", "ZIP")} value={checkout.delivery_zip} onChange={(e) => setCheckout({...checkout, delivery_zip: e.target.value})} data-testid="ck-zip" />
              <input className={inputCls} placeholder={t("الهاتف", "Phone")} value={checkout.delivery_phone} onChange={(e) => setCheckout({...checkout, delivery_phone: e.target.value})} data-testid="ck-phone" />
              <input className={inputCls} placeholder={t("ملاحظات (اختياري)", "Notes (optional)")} value={checkout.delivery_notes} onChange={(e) => setCheckout({...checkout, delivery_notes: e.target.value})} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowCheckout(false)} className="text-slate-300">{t("إلغاء", "Cancel")}</Button>
              <Button onClick={submit} disabled={submitting} className="btn-primary" data-testid="place-order-btn">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <><Send size={14} className="ml-2" /> {t("أرسل الطلب", "Place order")}</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
